import React from "react";
import { FaCheck } from "react-icons/fa6";
import { FaUser } from "react-icons/fa6";
import { FiDownload } from "react-icons/fi";
import { Link } from "react-router-dom";

type ButtonType = "primary" | "secondary" | "bluefil";

interface Button {
  label: string;
  type: ButtonType;
  link: string;
}

interface VisaReportCardProps {
  title: string;
  label: string;
  updatedDate: string;
  beneficiaryName?: string;
  labelColor?: string;
  labelBgColor?: string;
  buttonStyleSm?: React.CSSProperties;
  buttonStyleSecondarySm?: React.CSSProperties;
  buttonStyleFiledSm?: React.CSSProperties;
  buttons?: Button[];
  onDownload?: () => void;
  /** When true, show loading state on the download control */
  isDownloadingReport?: boolean;
}

const primaryLinkClass =
  "flex min-h-[44px] w-full items-center justify-center whitespace-nowrap rounded-[50px] border border-[#1B365D] px-2 py-2 text-center text-sm font-semibold leading-6 text-[#1B365D] hover:bg-[#1B365D] hover:text-white sm:w-auto sm:flex-auto";

const VisaReportCard: React.FC<VisaReportCardProps> = ({
  title,
  label,
  updatedDate,
  beneficiaryName,
  labelColor,
  labelBgColor,
  buttons = [],
  onDownload,
  isDownloadingReport = false,
}) => {
  const showDownload = typeof onDownload === "function";
  const hasPrimaryActions = buttons.length > 0;

  /** Same visual weight as the former header download control (spinner replaced icon in-slot). */
  const downloadSpinner = (
    <span
      className="inline-block h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-[#1B365D] border-t-transparent"
      aria-hidden
    />
  );

  return (
    <div className="visaCard flex h-full min-h-0 w-full flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="min-h-0 flex-1 flex flex-col">
        <div className="mb-1 min-h-[32px]">
          <h5 className="min-h-[24px] text-base font-semibold text-[#1F2937]">
            {title}
          </h5>
        </div>
        {beneficiaryName && (
          <p
            className="mb-2 flex items-center gap-1.5 text-sm text-[#6B7280]"
            aria-label={`Beneficiary ${beneficiaryName}`}
          >
            <FaUser className="text-[12px] text-[#6B7280]" aria-hidden />
            <span className="font-medium text-[#1B365D]">
              {beneficiaryName}
            </span>
          </p>
        )}
        <span
          className="inline-flex w-fit shrink-0 self-start rounded-full bg-gray-200 px-[7px] py-1 text-xs font-normal"
          style={{ backgroundColor: labelBgColor, color: labelColor }}
        >
          {label}
        </span>
        <p
          className="mt-3 flex items-center text-xs text-[#4B5563]"
          aria-label={`Checked ${updatedDate}`}
          title={`Checked: ${updatedDate}`}
        >
          <FaCheck className="mr-1.5 text-[12px] text-[#6B7280]" aria-hidden />
          {updatedDate}
        </p>
      </div>

      {(hasPrimaryActions || showDownload) && (
        <div
          className={`flex shrink-0 gap-2 pt-4 ${
            hasPrimaryActions && showDownload
              ? "flex-col items-stretch sm:flex-row sm:items-center"
              : "flex-col"
          }`}
        >
          {hasPrimaryActions ? (
            <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-stretch">
              {buttons.map((btn, idx) => (
                <Link key={idx} to={btn.link} className={primaryLinkClass}>
                  {btn.label}
                </Link>
              ))}
            </div>
          ) : null}

          {showDownload ? (
            hasPrimaryActions ? (
              <button
                type="button"
                onClick={onDownload}
                disabled={isDownloadingReport}
                aria-busy={isDownloadingReport}
                aria-label={
                  isDownloadingReport
                    ? "Preparing Eligibility Report"
                    : "Download Eligibility Report"
                }
                title="Download Eligibility Report"
                className="flex h-11 w-11 min-w-[44px] shrink-0 items-center justify-center self-end rounded-full border border-[#1B365D] text-[#1B365D] transition-colors hover:bg-[#1B365D] hover:text-white disabled:pointer-events-none disabled:opacity-60 sm:self-auto"
              >
                {isDownloadingReport ? downloadSpinner : (
                  <FiDownload className="text-lg" aria-hidden />
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={onDownload}
                disabled={isDownloadingReport}
                aria-busy={isDownloadingReport}
                title="Download Eligibility Report"
                aria-label={
                  isDownloadingReport
                    ? "Preparing Eligibility Report"
                    : "Download Eligibility Report"
                }
                className="flex w-full min-h-[44px] items-center justify-center rounded-[50px] border border-[#1B365D] px-3 py-2 text-sm font-semibold leading-6 text-[#1B365D] transition-colors hover:bg-[#1B365D] hover:text-white disabled:pointer-events-none disabled:opacity-60"
              >
                {isDownloadingReport ? (
                  downloadSpinner
                ) : (
                  <span>Download Eligibility Report</span>
                )}
              </button>
            )
          ) : null}
        </div>
      )}
    </div>
  );
};

export default VisaReportCard;
